import { useState, useEffect, useCallback } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Link2, Loader2, Check, Unlink, RefreshCw } from 'lucide-react';

/**
 * TikTok Login Kit connect — Sprint 68 (1/N). Connection ONLY: verifies a
 * real TikTok account (open_id + profile) and stores the token. Mensajes,
 * comentarios, publicación y anuncios son fases futuras separadas (ADR-145)
 * — este componente nunca promete nada de eso, solo la conexión real.
 * @param {object} props
 * @param {function} [props.onConnected]
 * @param {boolean} [props.compact]
 */
export default function TikTokLoginConnect({ onConnected, compact = false }) {
  const [status, setStatus] = useState('loading'); // loading | not_connected | connected | error
  const [displayName, setDisplayName] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [redirecting, setRedirecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const { data } = await api.get('/tenant/oauth/tiktok/account');
      if (data.connected) {
        setDisplayName(data.display_name);
        setAvatarUrl(data.avatar_url);
        setStatus('connected');
      } else {
        setStatus('not_connected');
      }
    } catch (e) {
      setStatus('error');
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('oauth') === 'tiktok') {
      if (params.get('connected') === '1') {
        load().then(() => onConnected?.());
      } else {
        toast.error('No se pudo completar la conexión con TikTok — vuelve a intentarlo.');
        setStatus('not_connected');
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('oauth'); url.searchParams.delete('connected'); url.searchParams.delete('reason');
      window.history.replaceState({}, '', url.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    setRedirecting(true);
    try {
      const { data } = await api.get('/tenant/oauth/tiktok/authorize', {
        params: { return_to: window.location.pathname },
      });
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo iniciar la conexión con TikTok');
      setRedirecting(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('¿Desconectar tu cuenta de TikTok?')) return;
    setDisconnecting(true);
    try {
      await api.post('/tenant/oauth/tiktok/disconnect');
      toast.success('TikTok desconectado');
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDisconnecting(false);
    }
  };

  if (status === 'loading') {
    return <div className="flex items-center gap-2 text-sm text-[#64748B] py-2"><Loader2 className="w-4 h-4 animate-spin" /> Buscando tu cuenta de TikTok...</div>;
  }

  if (status === 'error') {
    return (
      <div className={compact ? '' : 'bg-white border border-[#E2E8F0] rounded-xl p-5'}>
        <Button onClick={load} variant="outline" size="sm" className="gap-2"><RefreshCw className="w-4 h-4" /> Reintentar</Button>
      </div>
    );
  }

  if (status === 'not_connected') {
    return (
      <div className={compact ? '' : 'bg-white border border-[#E2E8F0] rounded-xl p-5'}>
        <p className="text-sm text-[#64748B] mb-3">
          Conecta tu cuenta de TikTok para verificar tu identidad ante TikTok. Esto es solo el primer paso —
          responder mensajes/comentarios, publicar y crear anuncios de TikTok se activan en pasos posteriores,
          por ahora esta conexión no hace nada de eso todavía.
        </p>
        <Button onClick={connect} disabled={redirecting} className="gap-2" data-testid="tiktok-connect-button">
          {redirecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
          Conectar TikTok
        </Button>
      </div>
    );
  }

  return (
    <div className={compact ? '' : 'bg-white border border-[#E2E8F0] rounded-xl p-5'} data-testid="tiktok-connected">
      <div className="flex items-center justify-between border border-[#E2E8F0] rounded-lg px-3 py-2 bg-[#F8FAFC]">
        <div className="flex items-center gap-2">
          {avatarUrl && <img src={avatarUrl} alt="" className="w-6 h-6 rounded-full" />}
          <div>
            <p className="text-sm font-medium text-[#0F172A] flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5 text-emerald-600" /> {displayName || 'Conectado'}
            </p>
            <p className="text-[11px] text-[#94A3B8]">Cuenta verificada. Mensajes, publicaciones y anuncios llegan en próximos pasos.</p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={disconnect} disabled={disconnecting} title="Desconectar">
          {disconnecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlink className="w-3.5 h-3.5" />}
        </Button>
      </div>
    </div>
  );
}
